/**
 * Inventory Grid Component
 * Displays inventory items in a grid/list format with filtering and actions
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  CubeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  WrenchIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import Pagination from '../../../common/Pagination';
import EmptyState from '../../../common/EmptyState';

const InventoryGrid = ({
  items,
  productTypes,
  onEdit,
  onDelete,
  onStatusChange,
  onSelectItem,
  selectedItems = new Set(),
  selectAll = false,
  onSelectAll,
  viewMode = 'grid' // 'grid' or 'list'
}) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [sortField, setSortField] = useState('itemNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Column filter states
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState(new Set());
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  
  // Refs for filter dropdowns
  const locationFilterRef = useRef(null);
  const typeFilterRef = useRef(null);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationFilterRef.current && !locationFilterRef.current.contains(event.target)) {
        setShowLocationFilter(false);
      }
      if (typeFilterRef.current && !typeFilterRef.current.contains(event.target)) {
        setShowTypeFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return <CheckCircleIcon className="h-4 w-4 text-green-600" />;
      case 'IN_USE':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />;
      case 'MAINTENANCE':
        return <WrenchIcon className="h-4 w-4 text-orange-600" />;
      case 'OUT_OF_SERVICE':
        return <XCircleIcon className="h-4 w-4 text-red-600" />;
      case 'DAMAGED':
        return <XCircleIcon className="h-4 w-4 text-red-800" />;
      case 'RETIRED':
        return <XCircleIcon className="h-4 w-4 text-gray-600" />;
      default:
        return <CubeIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_USE':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'MAINTENANCE':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'OUT_OF_SERVICE':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'DAMAGED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'RETIRED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get valid status options for dropdown
  const getValidStatusOptions = (currentStatus) => {
    const validTransitions = {
      'AVAILABLE': ['AVAILABLE', 'IN_USE', 'MAINTENANCE', 'OUT_OF_SERVICE', 'DAMAGED'],
      'IN_USE': ['IN_USE', 'AVAILABLE'], // IN_USE items can only be returned to AVAILABLE
      'MAINTENANCE': ['MAINTENANCE', 'AVAILABLE', 'OUT_OF_SERVICE', 'DAMAGED'],
      'OUT_OF_SERVICE': ['OUT_OF_SERVICE', 'AVAILABLE', 'MAINTENANCE'],
      'DAMAGED': ['DAMAGED', 'MAINTENANCE', 'OUT_OF_SERVICE', 'AVAILABLE']
    };

    return validTransitions[currentStatus] || [currentStatus];
  };

  // Get unique locations
  const uniqueLocations = [...new Set(items.map(item => item.location).filter(Boolean))];
  
  // Get unique types  
  const uniqueTypes = productTypes || [];

  // Handle location filter toggle
  const handleLocationFilter = (location) => {
    const newSelected = new Set(selectedLocations);
    if (newSelected.has(location)) {
      newSelected.delete(location);
    } else {
      newSelected.add(location);
    }
    setSelectedLocations(newSelected);
  };

  // Handle type filter toggle
  const handleTypeFilter = (typeId) => {
    const newSelected = new Set(selectedTypes);
    if (newSelected.has(typeId)) {
      newSelected.delete(typeId);
    } else {
      newSelected.add(typeId);
    }
    setSelectedTypes(newSelected);
  };

  // Clear all filters
  const clearLocationFilters = () => {
    setSelectedLocations(new Set());
  };

  const clearTypeFilters = () => {
    setSelectedTypes(new Set());
  };

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let filtered = [...items];

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(item => item.status === filter);
    }

    // Apply column location filter
    if (selectedLocations.size > 0) {
      filtered = filtered.filter(item => selectedLocations.has(item.location));
    }

    // Apply column type filter
    if (selectedTypes.size > 0) {
      filtered = filtered.filter(item => selectedTypes.has(item.productTypeId));
    }

    // Apply type filter (legacy dropdown)
    if (selectedType !== 'all') {
      filtered = filtered.filter(item => item.productTypeId === selectedType);
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.itemNumber?.toLowerCase().includes(search) ||
        item.serialNumber?.toLowerCase().includes(search) ||
        item.location?.toLowerCase().includes(search) ||
        item.notes?.toLowerCase().includes(search) ||
        item.productType?.name?.toLowerCase().includes(search)
      );
    }

    // Sort items
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortField === 'productType') {
        aVal = a.productType?.name || '';
        bVal = b.productType?.name || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [items, filter, selectedType, searchTerm, sortField, sortDirection, selectedLocations, selectedTypes]);

  // Pagination
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredAndSortedItems.slice(start, end);
  }, [filteredAndSortedItems, currentPage, itemsPerPage]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters - Mobile Optimized */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 sm:items-center">
          {/* Search - Mobile Optimized */}
          <div className="w-full sm:flex-1 sm:min-w-[200px]">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter - Mobile Optimized */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
          >
            <option value="all">All Status</option>
            <option value="AVAILABLE">Available</option>
            <option value="IN_USE">In Use</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="OUT_OF_SERVICE">Out of Service</option>
            <option value="DAMAGED">Damaged</option>
          </select>

          {/* Type Filter - Mobile Optimized */}
          {productTypes && productTypes.length > 0 && (
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
            >
              <option value="all">All Types</option>
              {productTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          )}

          {/* Filter Icon - Hidden on Mobile for Space */}
          <FunnelIcon className="hidden sm:block h-5 w-5 text-gray-400" />
        </div>
      </div>

      {/* Items Grid/List */}
      {paginatedItems.length === 0 ? (
        <EmptyState
          icon={CubeIcon}
          title="No inventory items found"
          description="Try adjusting your filters or add new items to get started."
        />
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginatedItems.map(item => (
            <div
              key={item.id}
              className={`bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow ${
                selectedItems.has(item.id) ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="space-y-3">
                {/* Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      #{item.itemNumber}
                    </h3>
                    <div className="flex flex-col">
                      <p className="text-sm text-gray-700">
                        {item.productType?.name || 'Unknown Type'}
                      </p>
                      {item.productVariant?.name && (
                        <p className="text-xs text-gray-500">
                          {item.productVariant.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {onSelectItem && (
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => onSelectItem(item.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    )}
                    {getStatusIcon(item.status)}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-1 text-sm">
                  {item.serialNumber && (
                    <div className="text-gray-600">
                      <span className="font-medium">Serial:</span> {item.serialNumber}
                    </div>
                  )}
                  {item.location && (
                    <div className="text-gray-600">
                      <span className="font-medium">Location:</span> {item.location}
                    </div>
                  )}
                  {item.condition && (
                    <div className="text-gray-600">
                      <span className="font-medium">Condition:</span> {item.condition}
                    </div>
                  )}
                </div>

                {/* Status Dropdown */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(item.status)}
                    <select
                      value={item.status}
                      onChange={(e) => onStatusChange && onStatusChange(item, e.target.value)}
                      disabled={item._count?.reservations > 0}
                      className={`flex-1 text-xs border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500 touch-manipulation ${
                        item._count?.reservations > 0 ? 'bg-red-50 cursor-not-allowed' :
                        item.status === 'IN_USE' ? 'bg-yellow-50' : ''
                      }`}
                      title={item._count?.reservations > 0 ? 'Cannot change status - item has active reservations' : ''}
                    >
                      {getValidStatusOptions(item.status).map(status => (
                        <option key={status} value={status}>
                          {status === 'AVAILABLE' ? 'Available' :
                           status === 'IN_USE' ? 'In Use' :
                           status === 'MAINTENANCE' ? 'Maintenance' :
                           status === 'OUT_OF_SERVICE' ? 'Out of Service' :
                           status === 'DAMAGED' ? 'Damaged' :
                           status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-2 pt-2 border-t">
                  <button
                    onClick={() => onEdit(item)}
                    className="flex-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded touch-manipulation"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(item)}
                    className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded touch-manipulation"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View - Table */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {onSelectItem && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={onSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                <th 
                  onClick={() => handleSort('itemNumber')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Item #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider relative">
                  <div className="flex items-center justify-between">
                    <span 
                      onClick={() => handleSort('productType')}
                      className="cursor-pointer hover:text-gray-700"
                    >
                      Type
                    </span>
                    <button
                      onClick={() => setShowTypeFilter(!showTypeFilter)}
                      className="ml-2 p-1 hover:bg-gray-200 rounded"
                    >
                      <FunnelIcon className="h-3 w-3 text-gray-400" />
                    </button>
                  </div>
                  {showTypeFilter && (
                    <div ref={typeFilterRef} className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 w-64">
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-900">FILTER BY TYPE</h4>
                          <button
                            onClick={clearTypeFilters}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {uniqueTypes.map(type => (
                            <label key={type.id} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedTypes.has(type.id)}
                                onChange={() => handleTypeFilter(type.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">{type.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Serial #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider relative">
                  <div className="flex items-center justify-between">
                    <span>Location</span>
                    <button
                      onClick={() => setShowLocationFilter(!showLocationFilter)}
                      className="ml-2 p-1 hover:bg-gray-200 rounded"
                    >
                      <FunnelIcon className="h-3 w-3 text-gray-400" />
                    </button>
                  </div>
                  {showLocationFilter && (
                    <div ref={locationFilterRef} className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 w-64">
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-900">FILTER BY LOCATION</h4>
                          <button
                            onClick={clearLocationFilters}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {uniqueLocations.map(location => (
                            <label key={location} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedLocations.has(location)}
                                onChange={() => handleLocationFilter(location)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">{location}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedItems.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  {onSelectItem && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => onSelectItem(item.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    #{item.itemNumber}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-col">
                      <span className="text-gray-900">{item.productType?.name || 'Unknown'}</span>
                      {item.productVariant?.name && (
                        <span className="text-xs text-gray-500">{item.productVariant.name}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {item.serialNumber || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {item.location || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      {getStatusIcon(item.status)}
                      <div className="ml-2 flex flex-col">
                        <select
                          value={item.status}
                          onChange={(e) => onStatusChange && onStatusChange(item, e.target.value)}
                          disabled={item._count?.reservations > 0 || (item.status === 'IN_USE' && !getValidStatusOptions(item.status).includes('AVAILABLE'))}
                          className={`text-xs border border-gray-300 rounded px-2 py-1 focus:ring-blue-500 focus:border-blue-500 ${
                            item._count?.reservations > 0 ? 'bg-red-50 cursor-not-allowed' : 
                            item.status === 'IN_USE' ? 'bg-yellow-50' : ''
                          }`}
                          title={item._count?.reservations > 0 ? 'Cannot change status - item has active reservations' : ''}
                        >
                          {getValidStatusOptions(item.status).map(status => (
                            <option key={status} value={status}>
                              {status === 'AVAILABLE' ? 'Available' : 
                               status === 'IN_USE' ? 'In Use' :
                               status === 'MAINTENANCE' ? 'Maintenance' :
                               status === 'OUT_OF_SERVICE' ? 'Out of Service' :
                               status === 'DAMAGED' ? 'Damaged' :
                               status}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEdit(item)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(item)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {filteredAndSortedItems.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalItems={filteredAndSortedItems.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default InventoryGrid;